/**
 * Менеджер экрана клуба/сообщества
 * Отображает статистику клуба, участников и онлайн пользователей
 */

import { logger } from "../../utils/logger.js";

export function createClubManager({
  safeFetch,
  buildAuthHeaders,
  getApiUrl,
  getCurrentUser,
  updateUserBalancesUI,
  documentRef = document
}) {
  
  /**
   * Загружает статистику клуба
   */
  async function loadClubStats() {
    try {
      const apiUrl = getApiUrl();
      const currentUser = getCurrentUser();
      
      logger.debug('[ClubManager] Загрузка статистики клуба для пользователя:', currentUser?.id);
      
      // Загружаем несколько данных параллельно
      const [onlineStats, userStats, leaderboard] = await Promise.all([
        // Статистика онлайн
        safeFetch(`${apiUrl}/online/stats`, {
          method: 'GET',
          headers: buildAuthHeaders()
        }),
        // Статистика пользователя
        currentUser ? safeFetch(`${apiUrl}/user/stats`, {
          method: 'GET',
          headers: buildAuthHeaders()
        }) : Promise.resolve({ success: false }),
        // Таблица лидеров (для ранга)
        safeFetch(`${apiUrl}/leaderboard/collection`, {
          method: 'GET',
          headers: buildAuthHeaders()
        })
      ]);

      logger.debug('[ClubManager] Ответы API:', {
        onlineStats: onlineStats.success,
        userStats: userStats.success,
        leaderboard: leaderboard.success
      });

      // Извлекаем данные из ответов (safeFetch оборачивает ответы)
      let onlineCount = 0;
      if (onlineStats.success && onlineStats.data) {
        // Структура: { success: true, data: { success: true, stats: {...} } } или { success: true, data: { stats: {...} } }
        let stats = null;
        if (onlineStats.data.data) {
          stats = onlineStats.data.data.stats || onlineStats.data.data;
        } else if (onlineStats.data.success && onlineStats.data.data) {
          stats = onlineStats.data.data.stats || onlineStats.data.data;
        } else {
          stats = onlineStats.data;
        }
        // getOnlineUsersStats возвращает объект с полями last5min, last15min и т.д.
        onlineCount = stats?.last15min || stats?.count || stats?.online || 0;
        logger.debug('[ClubManager] Онлайн пользователей:', onlineCount, 'из stats:', stats);
      }

      let userStatistics = null;
      if (userStats.success && userStats.data) {
        // Структура: { success: true, data: { success: true, stats: {...} } } или { success: true, data: { stats: {...} } }
        if (userStats.data.data) {
          userStatistics = userStats.data.data.stats || userStats.data.data;
        } else if (userStats.data.success && userStats.data.data) {
          userStatistics = userStats.data.data.stats || userStats.data.data;
        } else {
          userStatistics = userStats.data.stats || userStats.data;
        }
        logger.debug('[ClubManager] Статистика пользователя:', userStatistics);
      }

      let leaderboardData = [];
      if (leaderboard.success && leaderboard.data) {
        // Структура: { success: true, data: { success: true, leaderboard: [...] } } или { success: true, data: { leaderboard: [...] } }
        if (leaderboard.data.data) {
          leaderboardData = leaderboard.data.data.leaderboard || leaderboard.data.data || [];
        } else if (leaderboard.data.success && leaderboard.data.data) {
          leaderboardData = leaderboard.data.data.leaderboard || leaderboard.data.data || [];
        } else {
          leaderboardData = leaderboard.data.leaderboard || leaderboard.data || [];
        }
        logger.debug('[ClubManager] Таблица лидеров:', leaderboardData.length, 'участников');
      }

      // Находим ранг пользователя
      let userRank = null;
      if (currentUser && leaderboardData.length > 0) {
        const userIndex = leaderboardData.findIndex(
          entry => entry.user_id === currentUser.id || entry.id === currentUser.id
        );
        if (userIndex >= 0) {
          userRank = userIndex + 1;
        }
        logger.debug('[ClubManager] Ранг пользователя:', userRank);
      }

      const result = {
        online: onlineCount,
        trophies: userStatistics?.pvp?.wins || 0,
        rank: userRank,
        activity: userStatistics ? calculateActivity(userStatistics) : 0,
        members: leaderboardData.slice(0, 10) // Топ 10 участников
      };

      logger.debug('[ClubManager] Итоговая статистика:', result);
      return result;
    } catch (error) {
      logger.error('[ClubManager] Ошибка загрузки статистики клуба:', error);
      return {
        online: 0,
        trophies: 0,
        rank: null,
        activity: 0,
        members: []
      };
    }
  }

  /**
   * Рассчитывает активность пользователя (0-100%)
   */
  function calculateActivity(stats) {
    // Активность = (бои + победы + существа) / максимальное значение * 100
    const battles = stats.pvp?.battles || 0;
    const wins = stats.pvp?.wins || 0;
    const creatures = stats.collection?.total || 0;
    
    // Нормализуем значения (максимум 1000 для каждого параметра)
    const normalizedBattles = Math.min(1000, battles) / 10; // 0-100
    const normalizedWins = Math.min(1000, wins) / 10; // 0-100
    const normalizedCreatures = Math.min(100, creatures); // 0-100
    
    // Среднее значение
    const activity = Math.floor((normalizedBattles + normalizedWins + normalizedCreatures) / 3);
    return Math.min(100, activity);
  }

  /**
   * Рендерит экран клуба
   */
  async function renderClubScreen() {
    const currentUser = getCurrentUser();
    if (currentUser) {
      updateUserBalancesUI(currentUser);
    }

    const domCache = window.__domCacheManager;

    // Обновляем имя пользователя
    if (currentUser) {
      const playerName = currentUser.username || currentUser.first_name || 'GLITCHLING';
      domCache?.updateText('#player-name-club', playerName, documentRef) ||
      (() => {
        const el = documentRef.getElementById('player-name-club');
        if (el) el.textContent = playerName;
      })();
    }

    // Загружаем статистику клуба
    const stats = await loadClubStats();

    // Обновляем статистику через domCache
    domCache?.updateText('#club-trophies', String(stats.trophies || 0), documentRef) ||
    (() => {
      const el = documentRef.getElementById('club-trophies');
      if (el) el.textContent = stats.trophies || 0;
    })();

    const rankText = stats.rank ? `#${stats.rank}` : '#—';
    domCache?.updateText('#club-rank', rankText, documentRef) ||
    (() => {
      const el = documentRef.getElementById('club-rank');
      if (el) el.textContent = rankText;
    })();

    const activityText = `${stats.activity || 0}%`;
    domCache?.updateText('#club-activity', activityText, documentRef) ||
    (() => {
      const el = documentRef.getElementById('club-activity');
      if (el) el.textContent = activityText;
    })();

    const onlineText = `${stats.online || 0}/50`;
    domCache?.updateText('#club-online', onlineText, documentRef) ||
    (() => {
      const el = documentRef.getElementById('club-online');
      if (el) el.textContent = onlineText;
    })();

    const membersCountText = `${stats.members.length || 0}/50`;
    domCache?.updateText('#club-members-count', membersCountText, documentRef) ||
    (() => {
      const el = documentRef.getElementById('club-members-count');
      if (el) el.textContent = membersCountText;
    })();

    // Рендерим список участников
    const membersListEl = domCache?.getCachedElement('#members-list', documentRef) 
      || documentRef.getElementById('members-list');
    if (membersListEl) {
      if (stats.members.length === 0) {
        const emptyHTML = `
          <div style="padding: 40px; text-align: center; color: var(--tg-theme-hint-color);">
            <div style="font-size: 48px; margin-bottom: 16px;">👥</div>
            <div style="font-size: 18px; margin-bottom: 8px;">Нет участников</div>
            <div style="font-size: 14px;">Участники появятся здесь</div>
          </div>
        `;
        domCache?.updateHTML('#members-list', emptyHTML, documentRef) ||
        (membersListEl.innerHTML = emptyHTML);
      } else {
        const membersHtml = stats.members.map((member, index) => {
          const isCurrentUser = currentUser && (member.user_id === currentUser.id || member.id === currentUser.id);
          const rank = index + 1;
          const name = member.username || member.first_name || `Игрок #${member.id || member.user_id}`;
          const trophies = member.wins || member.trophies || 0;
          const creatures = member.creatures || member.collection_count || 0;

          return `
            <div style="
              display: flex;
              align-items: center;
              gap: 12px;
              padding: 12px;
              background: ${isCurrentUser ? 'rgba(77, 255, 253, 0.1)' : 'var(--tg-theme-bg-color, #1a1a2e)'};
              border: 1px solid ${isCurrentUser ? 'var(--tg-theme-button-color, #4dfffd)' : 'var(--tg-theme-hint-color, rgba(255,255,255,0.2))'};
              border-radius: 8px;
              margin-bottom: 8px;
            ">
              <div style="
                width: 40px;
                height: 40px;
                border-radius: 50%;
                background: ${isCurrentUser ? 'var(--tg-theme-button-color, #4dfffd)' : 'var(--tg-theme-hint-color, rgba(255,255,255,0.2))'};
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 700;
                font-size: 16px;
                color: ${isCurrentUser ? '#000' : '#fff'};
              ">${rank}</div>
              <div style="flex: 1;">
                <div style="font-weight: 600; font-size: 14px; margin-bottom: 4px;">
                  ${name} ${isCurrentUser ? '(Вы)' : ''}
                </div>
                <div style="font-size: 12px; color: var(--tg-theme-hint-color);">
                  🏆 ${trophies} трофеев • 🦉 ${creatures} существ
                </div>
              </div>
            </div>
          `;
        }).join('');

        domCache?.updateHTML('#members-list', membersHtml, documentRef) ||
        (membersListEl.innerHTML = membersHtml);
      }
    }
  }

  return {
    renderClubScreen,
    loadClubStats
  };
}

